const API_URL = process.env.REACT_APP_BACKEND_URL || '';
export const INQUIRY_TIMEOUT_MS = 20000;
const pendingReceipts = new Map();

const newRequestId = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
};

export const uncertainInquiryMessage = 'We could not confirm receipt. Your inquiry may already be saved. Keep this page open and retry with the same details, or call (925) 240-2990 before submitting again.';

/** One attempt only. Retain a receipt key for an explicit unchanged retry in this page session.
 * No contact information or credentials are written to browser storage or diagnostics.
 * Reloading/closing the page loses this in-memory retry protection; call the club if uncertain.
 */
export const submitLead = async (payload) => {
  const body = JSON.stringify(payload);
  let receipt = pendingReceipts.get(body);
  if (!receipt) {
    receipt = { key: `${Date.now()}-${newRequestId()}` };
    // Keep memory bounded; no automatic retries are ever scheduled.
    if (pendingReceipts.size >= 20) return { response: { ok: false, status: 0 }, data: { success: false, outcome: 'uncertain', detail: uncertainInquiryMessage } };
    pendingReceipts.set(body, receipt);
  }
  const requestId = newRequestId();
  const controller = new AbortController();
  let timer;
  let expired = false;
  try {
    const request = (async () => {
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': receipt.key, 'X-Request-ID': requestId },
        body,
        signal: controller.signal,
      });
      let data = {};
      try { data = await response.json(); } catch { /* Treat missing receipt as uncertain. */ }
      if (!data || typeof data !== 'object' || Array.isArray(data)) data = {};
      if ('success' in data) data.success = data.success === true;
      if (typeof data.message !== 'string') delete data.message;
      if ('detail' in data && typeof data.detail !== 'string') {
        data.detail = response.status === 422
          ? 'Please check your information and try again.'
          : 'Something went wrong. Please try again.';
      }
      if (response.ok && data.success) {
        if (!expired) pendingReceipts.delete(body);
      } else if (response.status >= 500 || response.status === 408 || (response.ok && !data.success)) {
        data = { success: false, detail: uncertainInquiryMessage, outcome: 'uncertain' };
      }
      // Only our own random reference is appended; never echo response exceptions/body data.
      if (data.outcome === 'uncertain') data.detail += ` Reference: ${requestId}`;
      return { response, data };
    })();
    const deadline = new Promise((resolve, reject) => {
      timer = setTimeout(() => { expired = true; controller.abort(); reject(new Error('inquiry_deadline')); }, INQUIRY_TIMEOUT_MS);
    });
    return await Promise.race([request, deadline]);
  } catch {
    return { response: { ok: false, status: 0 }, data: { success: false, outcome: 'uncertain', detail: `${uncertainInquiryMessage} Reference: ${requestId}` } };
  } finally {
    clearTimeout(timer);
  }
};
